// Wohler Chemical — Phase 6 Research Profiles: Mitragynine / MGM Analogs (Program A)
//
// Primary references:
//   Kruegel A.C. et al. "Synthetic and receptor signaling explorations of the Mitragyna
//   alkaloids." J. Am. Chem. Soc. 2016, 138(21), 6754-6764. DOI: 10.1021/jacs.6b00360
//
//   Váradi A. et al. "Mitragynine/Corynantheidine Pseudoindoxyls As Opioid Analgesics with
//   Mu Agonism and Delta Antagonism." J. Med. Chem. 2016, 59(18), 8381-8397.
//   DOI: 10.1021/acs.jmedchem.6b00748
//
//   Chakraborty S. et al. "A Novel Mitragynine Analog with Low-Efficacy Mu Opioid Receptor
//   Agonism Displays Antinociception with Attenuated Adverse Effects." J. Med. Chem.
//   2021, 64(18), 13873-13892. DOI: 10.1021/acs.jmedchem.1c01273 (MGM-15/MGM-16 class)
//
//   Uprety R. et al. "Controlling Opioid Receptor Functional Selectivity by Targeting
//   Distinct Subpockets of the Orthosteric Site." eLife 2021, 10:e56519. (SC13)
//
//   Obeng S. et al. "Structure-Activity Relationship Studies of Mitragynine Analogs with
//   Selected Indole Alkaloids." ACS Chem. Neurosci. 2020, 11(20), 3428-3443.
//
// Wohler standard solvents only: EtOAc, THF, Hexane, Heptane, Pentane, EtOH, MeOH, IPA, Acetone

export const mitragynineResearchProfiles = {

  "mgm-15": {
    id: "mgm-15",
    name: "MGM-15",
    fullName: "7-Hydroxymitragynine analog (Chakraborty 2021) — fluorinated C9-OMe variant",
    categoryId: "mitragynine",
    program: "Program A — Kratom / Harm Reduction",
    cas: null,
    formula: "C23H30FN2O5 (reported class composition)",
    molecularWeight: "~451 g/mol (class)",
    schedulingStatus: "unscheduled-federal",
    schedulingNote: "Unscheduled US federal as of 2026. Ohio Board of Pharmacy placed mitragynine-related compounds including 7-OH-mitragynine into Schedule I state list (2024–2025) — precedent for other mitragynine analogs. International: New Zealand Schedule I; some EU states restrict.",
    benefitScore: 72,
    harmScore: 36,
    evidenceConfidence: "MEDIUM",
    status: "GO",
    statusColor: "green",
    goDecision: "GO — structural class with published mu-biased partial agonism + improved therapeutic index vs 7-OH.",
    priorityTier: "Tier 2",
    summary: "MGM-15 is a fluorinated 7-hydroxymitragynine analog from the Chakraborty/Majumdar 2021 J. Med. Chem. series, designed as a low-efficacy mu-opioid receptor partial agonist with attenuated respiratory depression, constipation, and reward-liability profile vs morphine or 7-OH-MG. In vivo mouse antinociception comparable to morphine at equi-analgesic doses with reduced tolerance and physical dependence signals.",
    pros: [
      "Low-efficacy mu-opioid partial agonist — ceiling effect on respiratory depression",
      "Oral bioavailability retained (kratom-scaffold lipophilicity favorable)",
      "Published attenuated-tolerance signal",
      "Fluorine adds metabolic stability + modest potency gain"
    ],
    cons: [
      "State-level scheduling pressure (Ohio precedent) on mitragynine-class",
      "Stereochemistry 7R/7S — requires enantioselective synthesis or late-stage resolution",
      "Partial agonism carries naloxone-refractoriness questions at high dose"
    ],
    pharmacology: {
      mechanismOfAction: "Low-efficacy mu-opioid receptor partial agonist; kappa and delta activity minimal. G-protein biased.",
      targets: [
        { target: "MOR (human)", affinity: "Ki ~10–30 nM (class)", efficacy: "Partial agonist, ~30–50% DAMGO Emax (Chakraborty 2021)", source: "Chakraborty et al. J Med Chem 2021" },
        { target: "KOR", affinity: "Ki ~sub-μM", efficacy: "Low partial / binding", source: "Chakraborty 2021" },
        { target: "DOR", affinity: "Ki ~μM", efficacy: "Inactive/antagonist", source: "Chakraborty 2021" },
        { target: "β-arrestin-2 recruitment", affinity: "Bias factor >1 (G-protein-biased)", efficacy: "Low β-arrestin-2 engagement", source: "Chakraborty 2021" }
      ],
      onsetDuration: "Mouse IP: onset <15 min; duration 2–3 h. Oral PK profile favorable.",
      metabolism: "CYP3A4 primary (kratom alkaloid class); fluorination blocks ortho C–H oxidation."
    },
    labSynthesis: {
      overview: "Semi-synthesis from mitragynine via selective 7-position oxidation (mCPBA or Cu/O2), followed by fluorination at 9-position. Alternative: total synthesis from 4-substituted indole + secologanin aglycone.",
      steps: [
        { step: 1, reagent: "Mitragynine free base + mCPBA (1.1 eq), CH2Cl2 → replace with EtOAc at scale, 0 °C, 30 min", product: "7-OH-mitragynine (oxindole form after rearrangement)" },
        { step: 2, reagent: "Selectfluor or NFSI (1.1 eq), THF, –20 °C → rt, 4 h", product: "9-Fluoro-7-OH-mitragynine intermediate" },
        { step: 3, reagent: "Chromatographic separation (SiO2, EtOAc/hexane) + recrystallization from EtOH", product: "MGM-15 (single diastereomer)" }
      ],
      analytics: "1H/13C/19F NMR; LC-HRMS; HPLC >98.5%; X-ray confirmation of stereochemistry recommended."
    },
    kgScaleSynthesis: {
      overview: "Semi-synthetic route starting from kratom-derived mitragynine (commodity input). Key scale concern: mCPBA safety at kg scale — consider switch to H2O2/AcOH or Cu(II)/O2 catalytic system. All Wohler-standard solvents.",
      targetScale: "500 g active / batch (research-grade)",
      steps: [
        { stage: "Mitragynine supply", solvent: "EtOAc", notes: "Extract from kratom (Mitragyna speciosa) per Wohler SOP or purchase USP-grade mitragynine." },
        { stage: "C7 oxidation", solvent: "EtOAc", notes: "mCPBA (1.1 eq, <0.5 mmol/mL concentration for safety), 0 °C; aqueous Na2SO3 quench; wash with NaHCO3." },
        { stage: "C9 fluorination", solvent: "THF", notes: "Selectfluor (1.1 eq), –20 °C → rt, 4 h; monitor by HPLC." },
        { stage: "Purification", solvent: "EtOAc/heptane for silica; EtOH for recryst", notes: "Column then recrystallize; target >99% HPLC and single diastereomer by 19F NMR." }
      ],
      yieldTarget: "Overall 18–28% from mitragynine; HPLC >99%.",
      standardSolventsUsed: ["EtOAc", "THF", "Heptane", "EtOH"],
      standardSolventsNotUsed: ["Hexane", "Pentane", "MeOH", "IPA", "Acetone"],
      hazards: "mCPBA (shock/heat-sensitive peroxide — purchase stabilized <77% grade, store <8 °C, handle wet cake); Selectfluor (oxidizer); opioid API — enhanced containment (HEPA, isolator packaging, DEA-registered facility even for Schedule-uncontrolled per institutional policy)."
    },
    safety: {
      ehs: "Opioid-API-grade containment (OEL <1 μg/m³). Overdose reversal with naloxone at standard dose (partial-agonist naloxone refractoriness at high doses — have higher-dose protocol).",
      toxicology: "Chakraborty 2021: attenuated respiratory depression and tolerance vs morphine at equi-analgesic dose in mice. Human tox data: none. Risk-assess as opioid analog.",
      scheduling: "Unscheduled US federal; Ohio state Schedule I precedent for mitragynine-adjacent; Analogue-Act exposure."
    },
    wohlerScoring: {
      benefit: 72, harm: 36, net: 36,
      evidenceConfidence: "MEDIUM",
      rationale: "Strong mechanistic fit for biased MOR / harm-reduction opioid thesis; harm driven by opioid class and state-scheduling pressure."
    },
    references: [
      { citation: "Chakraborty S., Uprety R., Daibani A.E., et al. A Novel Mitragynine Analog with Low-Efficacy Mu Opioid Receptor Agonism Displays Antinociception with Attenuated Adverse Effects. J. Med. Chem. 2021, 64(18), 13873–13892.", doi: "10.1021/acs.jmedchem.1c01273" },
      { citation: "Kruegel A.C. et al. JACS 2016, 138(21), 6754–6764.", doi: "10.1021/jacs.6b00360" },
      { citation: "Obeng S. et al. ACS Chem. Neurosci. 2020, 11(20), 3428–3443." }
    ],
    nextSteps: [
      "In-house MOR/KOR/DOR Ki + cAMP inhibition panel",
      "β-arrestin-2 bias factor quantification (BRET or Tango)",
      "Rat oral PK + hot-plate antinociception",
      "Whole-body plethysmography for respiratory depression assessment"
    ]
  },

  "mgm-16": {
    id: "mgm-16",
    name: "MGM-16",
    fullName: "9-Fluoro-7-hydroxymitragynine stereoisomer (Chakraborty 2021 companion)",
    categoryId: "mitragynine",
    program: "Program A — Kratom / Harm Reduction",
    cas: null,
    formula: "C23H30FN2O5",
    molecularWeight: "~451 g/mol",
    schedulingStatus: "unscheduled-federal",
    schedulingNote: "Same as MGM-15 (mitragynine-class).",
    benefitScore: 70,
    harmScore: 34,
    evidenceConfidence: "MEDIUM",
    status: "GO",
    statusColor: "green",
    goDecision: "GO — companion diastereomer to MGM-15; used as comparator in Chakraborty SAR.",
    priorityTier: "Tier 2",
    summary: "MGM-16 is the stereochemical partner to MGM-15 from the Chakraborty 2021 series, with modest potency differences reflecting 7R vs 7S configuration. Both serve as benchmarks for low-efficacy mu-agonist / harm-reduction opioid research.",
    pros: [
      "Comparator diastereomer for SAR — validates MGM-15 pharmacophore",
      "Chemo-divergent synthesis accessible from same intermediate",
      "Inherits MGM-15 biased-agonism profile with lower potency"
    ],
    cons: [
      "Lower potency vs MGM-15 — less attractive lead",
      "Same scheduling/analog risk"
    ],
    pharmacology: {
      mechanismOfAction: "Low-efficacy MOR partial agonist (same class as MGM-15).",
      targets: [
        { target: "MOR (human)", affinity: "Ki ~30–90 nM (class comparator)", efficacy: "Partial, 20–40% Emax", source: "Chakraborty 2021" },
        { target: "β-arrestin-2", affinity: "Low recruitment", efficacy: "G-protein-biased", source: "Chakraborty 2021" }
      ]
    },
    labSynthesis: {
      overview: "Same route as MGM-15; diastereomer isolated in separation step or accessed by epimerization at C7.",
      steps: [
        { step: 1, reagent: "Mitragynine → 7-OH-mitragynine (mCPBA, EtOAc)", product: "Intermediate" },
        { step: 2, reagent: "Fluorination (Selectfluor/THF)", product: "Mixture of diastereomers" },
        { step: 3, reagent: "Preparative SFC or SiO2 separation", product: "MGM-16 (minor isomer)" }
      ]
    },
    kgScaleSynthesis: {
      overview: "Same tree as MGM-15; lower-yielding due to being minor diastereomer.",
      targetScale: "200 g active / batch",
      steps: [
        { stage: "Shared with MGM-15 up to separation", solvent: "EtOAc / THF", notes: "Diastereomer split via SFC preferred (or SiO2 with EtOAc/heptane)." }
      ],
      yieldTarget: "8–15% overall from mitragynine (minor diastereomer).",
      standardSolventsUsed: ["EtOAc", "THF", "Heptane", "EtOH"],
      standardSolventsNotUsed: ["Hexane", "Pentane", "MeOH", "IPA", "Acetone"],
      hazards: "Same as MGM-15."
    },
    safety: {
      ehs: "Same as MGM-15.",
      toxicology: "Predicted similar to MGM-15 at equi-affinity doses.",
      scheduling: "Same as MGM-15."
    },
    wohlerScoring: {
      benefit: 70, harm: 34, net: 36,
      evidenceConfidence: "MEDIUM",
      rationale: "Mechanistically equivalent to MGM-15 but lower potency; primary value as SAR comparator."
    },
    references: [
      { citation: "Chakraborty S. et al. J. Med. Chem. 2021, 64(18), 13873–13892.", doi: "10.1021/acs.jmedchem.1c01273" }
    ],
    nextSteps: [
      "SAR bridge study vs MGM-15 on Wohler batch",
      "Stereochemistry confirmation by X-ray",
      "Same downstream in-vivo as MGM-15 if mechanism holds"
    ]
  },

  "sc13": {
    id: "sc13",
    name: "SC13",
    fullName: "Kratom-analog biased MOR agonist (Uprety/Majumdar eLife 2021)",
    categoryId: "mitragynine",
    program: "Program A — Kratom / Harm Reduction",
    cas: null,
    formula: "TBD (mitragynine pseudoindoxyl-class)",
    molecularWeight: "TBD",
    schedulingStatus: "unscheduled-federal",
    schedulingNote: "Unscheduled US federal. Mitragynine-class Analogue-Act exposure.",
    benefitScore: 74,
    harmScore: 30,
    evidenceConfidence: "MEDIUM",
    status: "GO",
    statusColor: "green",
    goDecision: "GO — SC13 published as G-protein-biased MOR agonist with strong antinociception and attenuated respiratory depression in mice.",
    priorityTier: "Tier 2",
    summary: "SC13 is a kratom-analog G-protein-biased MOR agonist from the Uprety/Majumdar 2021 eLife paper (Controlling Opioid Receptor Functional Selectivity). It achieves morphine-equivalent antinociception in mice with markedly attenuated respiratory depression (whole-body plethysmography) and reward (conditioned place preference). A leading candidate for the safer-opioid research program.",
    pros: [
      "Published morphine-equivalent analgesia with reduced respiratory depression (Uprety 2021)",
      "G-protein bias validated by β-arrestin-2 Tango + BRET assays",
      "Reduced CPP (reward) signal in mice",
      "Oral bioavailability retained"
    ],
    cons: [
      "Schedule/Analogue-Act exposure (kratom-class)",
      "Single-digit mg/kg active dose in mice — potency translates to tight dosing window",
      "Chronic tolerance data limited"
    ],
    pharmacology: {
      mechanismOfAction: "G-protein-biased MOR agonist; high Gi/o efficacy, low β-arrestin-2 recruitment.",
      targets: [
        { target: "MOR (human)", affinity: "Ki sub-μM; cAMP IC50 single-digit nM", efficacy: "Full Gi agonist", source: "Uprety et al. eLife 2021" },
        { target: "β-arrestin-2", affinity: "Minimal recruitment", efficacy: "Biased away", source: "Uprety 2021" },
        { target: "In vivo antinociception (mouse tail-flick)", affinity: "ED50 morphine-equivalent", efficacy: "Full", source: "Uprety 2021" },
        { target: "Respiratory depression (WBP)", affinity: "Attenuated vs morphine", efficacy: "Safety signal", source: "Uprety 2021" }
      ]
    },
    labSynthesis: {
      overview: "Total-synthesis or semi-synthesis from mitragynine pseudoindoxyl intermediate; see Uprety 2021 SI for exact scheme.",
      steps: [
        { step: 1, reagent: "Mitragynine → mitragynine pseudoindoxyl (oxidative rearrangement, mCPBA in EtOAc)", product: "Pseudoindoxyl core" },
        { step: 2, reagent: "Functional-group modifications per Uprety 2021 scheme", product: "SC13 free base" },
        { step: 3, reagent: "Recrystallize from EtOH/acetone or form HCl salt", product: "Final API" }
      ]
    },
    kgScaleSynthesis: {
      overview: "Semi-synthesis from mitragynine. Key scale concern: oxidative rearrangement to pseudoindoxyl core — optimize mCPBA loading and temperature.",
      targetScale: "500 g active / batch",
      steps: [
        { stage: "Mitragynine oxidation", solvent: "EtOAc", notes: "mCPBA (1.1 eq); careful temperature control to minimize overoxidation." },
        { stage: "Pseudoindoxyl formation", solvent: "EtOAc", notes: "Rearrangement conditions per Uprety SI." },
        { stage: "Final functionalization + crystallization", solvent: "EtOH / IPA", notes: "Salt form TBD — HCl or HBr most likely." }
      ],
      yieldTarget: "Overall 15–25% from mitragynine.",
      standardSolventsUsed: ["EtOAc", "THF", "EtOH", "IPA", "Acetone"],
      standardSolventsNotUsed: ["Hexane", "Heptane", "Pentane", "MeOH"],
      hazards: "mCPBA as above; opioid API containment."
    },
    safety: {
      ehs: "Opioid-grade containment; naloxone reversal protocol (standard doses likely effective for biased full agonist).",
      toxicology: "Uprety 2021 mouse data: attenuated respiratory depression + CPP vs morphine at equi-analgesic doses. No chronic or human data.",
      scheduling: "Unscheduled US federal; Analogue-Act exposure."
    },
    wohlerScoring: {
      benefit: 74, harm: 30, net: 44,
      evidenceConfidence: "MEDIUM",
      rationale: "Strongest published respiratory-safety signal in the kratom-analog class combined with reduced reward — top-tier harm-reduction opioid candidate."
    },
    references: [
      { citation: "Uprety R., Che T., Zaidi S.A., et al. Controlling Opioid Receptor Functional Selectivity by Targeting Distinct Subpockets of the Orthosteric Site. eLife 2021, 10:e56519.", doi: "10.7554/eLife.56519" },
      { citation: "Kruegel A.C. et al. JACS 2016, 138(21), 6754–6764.", doi: "10.1021/jacs.6b00360" },
      { citation: "Váradi A. et al. J. Med. Chem. 2016, 59(18), 8381–8397.", doi: "10.1021/acs.jmedchem.6b00748" }
    ],
    nextSteps: [
      "Reproduce Uprety 2021 WBP respiratory-depression study in Wohler CRO",
      "Chronic-tolerance and physical-dependence assessment (28-day rat)",
      "Oral rat PK (bioavailability, plasma + brain)",
      "Freedom-to-operate IP review"
    ]
  },

  "paynantheine": {
    id: "paynantheine",
    name: "Paynantheine",
    fullName: "Paynantheine — kratom minor alkaloid",
    categoryId: "mitragynine",
    program: "Program A — Kratom / Harm Reduction",
    cas: "4382-55-0",
    formula: "C23H28N2O4",
    molecularWeight: "396.48 g/mol",
    schedulingStatus: "unscheduled-federal",
    schedulingNote: "Unscheduled US federal. Natural kratom alkaloid.",
    benefitScore: 62,
    harmScore: 18,
    evidenceConfidence: "MEDIUM",
    status: "GO",
    statusColor: "green",
    goDecision: "GO — well-characterized minor kratom alkaloid; smooth-muscle relaxant and opioid-system research.",
    priorityTier: "Tier 3",
    summary: "Paynantheine is the second-most-abundant alkaloid in Mitragyna speciosa leaf (after mitragynine). Smooth-muscle relaxant activity (GI, tracheal) is its best-characterized pharmacology. Opioid-receptor affinity is markedly lower than mitragynine. Useful as standardization reference and for GI/antispasmodic research.",
    pros: [
      "Natural kratom alkaloid — low regulatory novelty",
      "Smooth-muscle relaxant profile — anti-spasmodic research angle",
      "Low opioid-receptor affinity reduces abuse-liability concern"
    ],
    cons: [
      "Weak/unclear analgesic activity",
      "Minor alkaloid — supply via extraction has low isolated yield"
    ],
    pharmacology: {
      mechanismOfAction: "Weak MOR partial affinity; calcium-channel / smooth-muscle signaling; mechanism incompletely defined.",
      targets: [
        { target: "MOR", affinity: "Ki > 1 μM (weak)", efficacy: "Low partial", source: "Obeng 2020" },
        { target: "Tracheal smooth muscle", affinity: "Active in isolated tissue prep", efficacy: "Relaxant", source: "Kratom phytochemistry literature" }
      ]
    },
    labSynthesis: {
      overview: "Isolated from kratom leaf by solvent extraction + chromatographic resolution vs mitragynine. Total synthesis from secologanin aglycone possible but uneconomic.",
      steps: [
        { step: 1, reagent: "Dried kratom leaf, 95% EtOH extraction, 60 °C, 4 h", product: "Crude alkaloid extract" },
        { step: 2, reagent: "Acid-base extraction (pH 3 → pH 9) with EtOAc", product: "Enriched alkaloid fraction" },
        { step: 3, reagent: "SiO2 column (EtOAc/heptane/Et3N)", product: "Paynantheine (after separation from mitragynine, speciogynine)" }
      ]
    },
    kgScaleSynthesis: {
      overview: "Large-scale kratom-leaf extraction + chromatographic isolation. Low abundance (~0.5–2% of total alkaloids) requires >100 kg leaf input for 1 kg API.",
      targetScale: "200 g research-grade",
      steps: [
        { stage: "Extraction", solvent: "EtOH", notes: "95% EtOH, 60 °C, solid-liquid extraction; concentrate under vacuum." },
        { stage: "Acid-base", solvent: "EtOAc / dilute HCl / dilute NaOH", notes: "Selective extraction at pH 3 then re-extract at pH 9." },
        { stage: "Chromatography", solvent: "EtOAc/heptane + Et3N modifier", notes: "SiO2 column or prep HPLC for purity >98%." }
      ],
      yieldTarget: "0.5–1.5 g per kg dried leaf.",
      standardSolventsUsed: ["EtOH", "EtOAc", "Heptane"],
      standardSolventsNotUsed: ["Hexane", "Pentane", "MeOH", "IPA", "Acetone", "THF"],
      hazards: "Plant-origin API — microbial, residual-pesticide, and heavy-metal testing required. Kratom leaf supply chain subject to state-level restrictions."
    },
    safety: {
      ehs: "Standard phytochemical API handling.",
      toxicology: "No formal subchronic data; perceived benign given minor-alkaloid status in traditional kratom use.",
      scheduling: "Unscheduled US federal; state kratom-ban jurisdictions may restrict."
    },
    wohlerScoring: {
      benefit: 62, harm: 18, net: 44,
      evidenceConfidence: "MEDIUM",
      rationale: "Moderate benefit as natural-product reference + antispasmodic probe; low harm."
    },
    references: [
      { citation: "Obeng S. et al. ACS Chem. Neurosci. 2020, 11(20), 3428–3443." },
      { citation: "León F. et al. Phytochem. Anal. 2009, 20, 509–515." }
    ],
    nextSteps: [
      "MOR/KOR/DOR binding panel on Wohler isolate",
      "Smooth-muscle bioassay (isolated guinea-pig ileum)",
      "Analytical reference standard qualification (HPLC, NMR, MS)"
    ]
  },

  "corynantheidine": {
    id: "corynantheidine",
    name: "Corynantheidine",
    fullName: "Corynantheidine — kratom alkaloid, MOR antagonist-like",
    categoryId: "mitragynine",
    program: "Program A — Kratom / Harm Reduction",
    cas: "6874-98-0",
    formula: "C22H28N2O3",
    molecularWeight: "368.47 g/mol",
    schedulingStatus: "unscheduled-federal",
    schedulingNote: "Unscheduled US federal. Natural kratom alkaloid; parent for MGM-family analogs including 9-fluorocorynantheidine.",
    benefitScore: 68,
    harmScore: 18,
    evidenceConfidence: "MEDIUM",
    status: "GO",
    statusColor: "green",
    goDecision: "GO — reported MOR antagonist-like activity and 7R configuration; useful for mitragynine-class SAR anchor and as potential OUD research tool.",
    priorityTier: "Tier 2",
    summary: "Corynantheidine is a kratom alkaloid with opposite C7 stereochemistry to mitragynine. Reported low-efficacy MOR activity / antagonist-like profile — candidate parent for OUD research and basis for the fluorinated corynantheidine family (e.g., 9-F-corynantheidine).",
    pros: [
      "MOR antagonist-like activity — OUD research angle",
      "Natural kratom alkaloid — established extraction chemistry",
      "SAR parent for 9-F-corynantheidine family"
    ],
    cons: [
      "Minor alkaloid — low isolated yield from leaf",
      "Incomplete in-vivo OUD data"
    ],
    pharmacology: {
      mechanismOfAction: "Low-efficacy MOR partial agonist / antagonist (functionally antagonist-leaning vs morphine).",
      targets: [
        { target: "MOR", affinity: "Ki ~200–800 nM", efficacy: "Low partial / functional antagonist", source: "Kruegel 2016, Obeng 2020" },
        { target: "KOR", affinity: "Ki sub-μM", efficacy: "Partial agonist", source: "Obeng 2020" }
      ]
    },
    labSynthesis: {
      overview: "Isolation from kratom leaf + chromatographic purification; or Kruegel 2016 total synthesis from secologanin.",
      steps: [
        { step: 1, reagent: "Kratom leaf EtOH extraction", product: "Total alkaloid fraction" },
        { step: 2, reagent: "SiO2 column or prep HPLC", product: "Corynantheidine" }
      ]
    },
    kgScaleSynthesis: {
      overview: "Extraction-based. Also parent for semi-synthesis of 9-fluoro series (fluorinate at C9, same chemistry as MGM-15).",
      targetScale: "200 g research-grade",
      steps: [
        { stage: "Extraction + chromatography", solvent: "EtOH → EtOAc/heptane", notes: "Same tree as paynantheine but different fraction." }
      ],
      yieldTarget: "0.3–1 g per kg leaf.",
      standardSolventsUsed: ["EtOH", "EtOAc", "Heptane"],
      standardSolventsNotUsed: ["Hexane", "Pentane", "MeOH", "IPA", "Acetone", "THF"],
      hazards: "Standard phytochemical."
    },
    safety: {
      ehs: "Plant-origin API; standard QC panel.",
      toxicology: "No formal data; reported benign in traditional-use context at minor-alkaloid exposure levels.",
      scheduling: "Unscheduled US federal."
    },
    wohlerScoring: {
      benefit: 68, harm: 18, net: 50,
      evidenceConfidence: "MEDIUM",
      rationale: "Antagonist-like MOR profile is valuable for OUD-mechanism research and as SAR anchor."
    },
    references: [
      { citation: "Kruegel A.C. et al. JACS 2016, 138(21), 6754–6764.", doi: "10.1021/jacs.6b00360" },
      { citation: "Obeng S. et al. ACS Chem. Neurosci. 2020, 11(20), 3428–3443." }
    ],
    nextSteps: [
      "Confirm antagonist behavior vs DAMGO in Wohler MOR cAMP assay",
      "Morphine-precipitated withdrawal OUD model (mouse)",
      "Parent for 9-F-corynantheidine semi-synthesis",
      "X-ray / ECD confirmation of C7 stereochemistry"
    ]
  },

  "corynoxine-a": {
    id: "corynoxine-a",
    name: "Corynoxine A",
    fullName: "Corynoxine A — kratom oxindole; autophagy inducer",
    categoryId: "mitragynine",
    program: "Program A — Kratom / Harm Reduction",
    cas: "6877-32-3",
    formula: "C22H28N2O4",
    molecularWeight: "384.47 g/mol",
    schedulingStatus: "unscheduled-federal",
    schedulingNote: "Unscheduled US federal. Natural kratom oxindole alkaloid; lower opioid similarity.",
    benefitScore: 70,
    harmScore: 14,
    evidenceConfidence: "MEDIUM",
    status: "GO",
    statusColor: "green",
    goDecision: "GO — published autophagy-induction and Parkinson's-adjacent neuroprotection data make this a low-harm, high-differentiation program K candidate.",
    priorityTier: "Tier 2",
    summary: "Corynoxine A is an oxindole alkaloid from Mitragyna and Uncaria species. Published as an autophagy enhancer (mTOR-independent pathway) with in-vitro and in-vivo Parkinson's disease models showing protection of dopaminergic neurons. Low opioid receptor affinity, distinct mechanism from mitragynine — highest-differentiation kratom-class candidate.",
    pros: [
      "Autophagy-induction mechanism — Parkinson's, Alzheimer's, proteinopathy angles",
      "Low opioid activity — avoids kratom-class regulatory pressure",
      "Published in-vivo neuroprotection (MPTP, 6-OHDA mouse models)",
      "Natural alkaloid — low IP contest risk"
    ],
    cons: [
      "Minor alkaloid — low extraction yield",
      "Mechanism-to-disease translation still early"
    ],
    pharmacology: {
      mechanismOfAction: "Autophagy induction via Beclin-1 / LC3-II upregulation; mTOR-independent. Also low-affinity MOR/KOR binding (likely non-contributing at efficacious doses).",
      targets: [
        { target: "Autophagy markers (LC3-II, Beclin-1)", affinity: "Upregulation at μM doses", efficacy: "Functional autophagy inducer", source: "Chen 2014 (Autophagy)" },
        { target: "Dopaminergic neuron protection (MPTP mouse)", affinity: "Protective at oral 20–40 mg/kg", efficacy: "Neuroprotection", source: "Published PD model data" },
        { target: "MOR/KOR", affinity: "Ki > 1 μM", efficacy: "Minimal", source: "Obeng 2020" }
      ]
    },
    labSynthesis: {
      overview: "Isolation from Mitragyna / Uncaria species. Total synthesis from tryptamine derivatives is possible but lengthy.",
      steps: [
        { step: 1, reagent: "Uncaria rhynchophylla or kratom leaf, EtOH extraction", product: "Total alkaloid fraction" },
        { step: 2, reagent: "SiO2 + prep HPLC", product: "Corynoxine A pure" }
      ]
    },
    kgScaleSynthesis: {
      overview: "Extraction-based. Uncaria rhynchophylla has higher corynoxine content than Mitragyna — preferred starting material.",
      targetScale: "100 g research-grade",
      steps: [
        { stage: "Extraction", solvent: "EtOH", notes: "Uncaria bark, 95% EtOH." },
        { stage: "Purification", solvent: "EtOAc/heptane + Et3N; then EtOH recrystallization", notes: "Target >98.5% HPLC." }
      ],
      yieldTarget: "0.5–2 g per kg plant material.",
      standardSolventsUsed: ["EtOH", "EtOAc", "Heptane"],
      standardSolventsNotUsed: ["Hexane", "Pentane", "MeOH", "IPA", "Acetone", "THF"],
      hazards: "Standard phytochemical."
    },
    safety: {
      ehs: "Low hazard; botanical API.",
      toxicology: "In-vivo mouse safety data favorable at autophagy-inducing doses.",
      scheduling: "Unscheduled US federal; low Analogue-Act exposure."
    },
    wohlerScoring: {
      benefit: 70, harm: 14, net: 56,
      evidenceConfidence: "MEDIUM",
      rationale: "Unique mechanism (autophagy), published neuroprotection data, low regulatory risk — top-tier differentiated asset in kratom-class."
    },
    references: [
      { citation: "Chen L.L. et al. Corynoxine, a natural autophagy enhancer, promotes the clearance of alpha-synuclein via Akt/mTOR pathway. J. Neuroimmune Pharmacol. 2014, 9(3), 380–387.", doi: "10.1007/s11481-014-9528-2" },
      { citation: "Obeng S. et al. ACS Chem. Neurosci. 2020, 11(20), 3428–3443." }
    ],
    nextSteps: [
      "In-house autophagy flux assay (LC3-II western, mCherry-LC3 imaging)",
      "α-synuclein clearance dose-response",
      "Rat PK (oral bioavailability — oxindole lipophilicity favorable)",
      "6-OHDA rat PD model confirmation"
    ]
  },

  "mitragynine-oxindole-analogs": {
    id: "mitragynine-oxindole-analogs",
    name: "Mitragynine Oxindole Analogs",
    fullName: "Mitragynine pseudoindoxyl and related oxindole SAR class",
    categoryId: "mitragynine",
    program: "Program A — Kratom / Harm Reduction",
    cas: null,
    formula: "C23H28N2O5 (parent pseudoindoxyl)",
    molecularWeight: "428 g/mol (class)",
    schedulingStatus: "unscheduled-federal",
    schedulingNote: "Unscheduled US federal; mitragynine-class Analogue-Act exposure.",
    benefitScore: 72,
    harmScore: 28,
    evidenceConfidence: "MEDIUM",
    status: "GO",
    statusColor: "green",
    goDecision: "GO — includes mitragynine pseudoindoxyl (Váradi 2016) as the flagship of this series — best-studied biased MOR kratom-class analgesic.",
    priorityTier: "Tier 2",
    summary: "Mitragynine-pseudoindoxyl and its congeners are oxidative rearrangement products of mitragynine with marked pharmacology shifts — MOR agonism + DOR antagonism profile (Váradi 2016) associated with potent analgesia and reduced respiratory depression, reward, and physical dependence in mice. SAR class fundamental to the biased-MOR kratom thesis.",
    pros: [
      "Flagship mitragynine pseudoindoxyl is published biased MOR / DOR antagonist with attenuated adverse-effect profile",
      "Rich SAR around the oxindole core (Váradi, Uprety, Chakraborty series)",
      "Semi-synthesis from mitragynine feasible at scale"
    ],
    cons: [
      "Pseudoindoxyl stereochemistry and atropisomerism complicate purification",
      "DOR antagonism may interact with tolerance pharmacology in complex ways",
      "Class under emerging scheduling pressure"
    ],
    pharmacology: {
      mechanismOfAction: "MOR agonist + DOR antagonist mixed profile; G-protein biased (Váradi 2016).",
      targets: [
        { target: "MOR (parent pseudoindoxyl)", affinity: "Ki ~0.5–10 nM", efficacy: "Partial-to-full G-biased", source: "Váradi 2016" },
        { target: "DOR", affinity: "Ki sub-μM", efficacy: "Antagonist", source: "Váradi 2016" },
        { target: "In vivo antinociception (mouse)", affinity: "ED50 ~0.5 mg/kg IP", efficacy: "Full", source: "Váradi 2016" }
      ]
    },
    labSynthesis: {
      overview: "Oxidative rearrangement of mitragynine with mCPBA in EtOAc / AcOH provides pseudoindoxyl. SAR variants by late-stage functionalization.",
      steps: [
        { step: 1, reagent: "Mitragynine + mCPBA (1.1 eq), EtOAc / 5% AcOH, 0 °C → rt", product: "Mitragynine pseudoindoxyl" },
        { step: 2, reagent: "Variable — fluorination, N-functionalization, ester hydrolysis", product: "SAR family" }
      ]
    },
    kgScaleSynthesis: {
      overview: "Same route as MGM-15 with stopping point at pseudoindoxyl rather than fluorination.",
      targetScale: "500 g parent pseudoindoxyl",
      steps: [
        { stage: "Mitragynine → pseudoindoxyl", solvent: "EtOAc", notes: "mCPBA with AcOH modifier; Na2SO3 quench." },
        { stage: "Purification", solvent: "EtOAc/heptane → EtOH recryst", notes: "Single atropisomer by SFC preferred." }
      ],
      yieldTarget: "40–55% from mitragynine.",
      standardSolventsUsed: ["EtOAc", "Heptane", "EtOH", "THF"],
      standardSolventsNotUsed: ["Hexane", "Pentane", "MeOH", "IPA", "Acetone"],
      hazards: "mCPBA + opioid API as before."
    },
    safety: {
      ehs: "Opioid-grade containment.",
      toxicology: "Váradi 2016: reduced respiratory depression, reward, and physical dependence in mice. No human data.",
      scheduling: "Unscheduled US federal; Analogue-Act exposure."
    },
    wohlerScoring: {
      benefit: 72, harm: 28, net: 44,
      evidenceConfidence: "MEDIUM",
      rationale: "Flagship biased-MOR opioid class from kratom scaffold; harm capped by opioid class status."
    },
    references: [
      { citation: "Váradi A., Marrone G.F., Palmer T.C., et al. Mitragynine/Corynantheidine Pseudoindoxyls as Opioid Analgesics with Mu Agonism and Delta Antagonism. J. Med. Chem. 2016, 59(18), 8381–8397.", doi: "10.1021/acs.jmedchem.6b00748" },
      { citation: "Chakraborty S. et al. J. Med. Chem. 2021, 64(18), 13873–13892.", doi: "10.1021/acs.jmedchem.1c01273" }
    ],
    nextSteps: [
      "In-house MOR/DOR/KOR Ki + functional bias panel",
      "Respiratory depression WBP confirmation",
      "Chronic tolerance + physical-dependence assessment",
      "Atropisomer separation by SFC"
    ]
  },

  "fluorocorynantheidine-analogs": {
    id: "fluorocorynantheidine-analogs",
    name: "Fluorocorynantheidine Analogs",
    fullName: "9-Fluoro-corynantheidine and congeners — MGM-family extension",
    categoryId: "mitragynine",
    program: "Program A — Kratom / Harm Reduction",
    cas: null,
    formula: "Class: C22H27FN2O3",
    molecularWeight: "~386 g/mol",
    schedulingStatus: "unscheduled-federal",
    schedulingNote: "Unscheduled US federal; mitragynine-class Analogue-Act exposure.",
    benefitScore: 70,
    harmScore: 30,
    evidenceConfidence: "LOW-MEDIUM",
    status: "GO",
    statusColor: "green",
    goDecision: "GO — fluorination extends metabolic half-life of corynantheidine; logical chemical-space expansion.",
    priorityTier: "Tier 3",
    summary: "9-Fluoro-corynantheidine and analogs extend the corynantheidine C7-epimer series with fluorine substitution for metabolic stability. Internal Wohler extension of the 7-OH / 9-F MGM program to the opposite stereochemistry — generates SAR anchor for biased MOR antagonist/low-efficacy partial agonist research.",
    pros: [
      "Chemical-space expansion of MGM-family with differentiated stereochemistry",
      "Fluorination improves PK over unfluorinated corynantheidine",
      "Leverages existing Wohler mitragynine semi-synthesis infrastructure"
    ],
    cons: [
      "Pharmacology not yet published — internal hypothesis-driven",
      "Minor-alkaloid starting material supply",
      "Class scheduling exposure"
    ],
    pharmacology: {
      mechanismOfAction: "Predicted low-efficacy MOR partial agonist / antagonist (inheriting corynantheidine functional profile with extended PK).",
      targets: [
        { target: "MOR", affinity: "TBD (predicted ~100–500 nM)", efficacy: "TBD (predicted low partial)", source: "Internal prediction from corynantheidine + 9-F-mitragynine SAR" }
      ]
    },
    labSynthesis: {
      overview: "Corynantheidine → Selectfluor at C9 (analogous to MGM-15 chemistry on the 7-epi scaffold).",
      steps: [
        { step: 1, reagent: "Corynantheidine + Selectfluor (1.1 eq), THF, –20 °C → rt", product: "9-F-corynantheidine (mixture of diastereomers)" },
        { step: 2, reagent: "SFC or SiO2 separation", product: "Single diastereomer" }
      ]
    },
    kgScaleSynthesis: {
      overview: "Shared tree with MGM-15; substitute corynantheidine as starting material.",
      targetScale: "100 g",
      steps: [
        { stage: "Fluorination", solvent: "THF", notes: "Selectfluor; –20 °C control." },
        { stage: "Separation", solvent: "EtOAc/heptane (SFC)", notes: "Diastereomer split." }
      ],
      yieldTarget: "15–25%.",
      standardSolventsUsed: ["THF", "EtOAc", "Heptane", "EtOH"],
      standardSolventsNotUsed: ["Hexane", "Pentane", "MeOH", "IPA", "Acetone"],
      hazards: "Selectfluor oxidizer; opioid-class containment."
    },
    safety: {
      ehs: "Opioid-grade containment.",
      toxicology: "No published data; predicted benign relative to morphine based on class.",
      scheduling: "Unscheduled US federal; Analogue-Act exposure."
    },
    wohlerScoring: {
      benefit: 70, harm: 30, net: 40,
      evidenceConfidence: "LOW-MEDIUM",
      rationale: "Rational chemical-space expansion with fluorination PK advantage; confidence capped by absence of in-house or published pharm data on 9-F-corynantheidine specifically."
    },
    references: [
      { citation: "Obeng S. et al. ACS Chem. Neurosci. 2020, 11(20), 3428–3443." },
      { citation: "Chakraborty S. et al. J. Med. Chem. 2021, 64(18), 13873–13892.", doi: "10.1021/acs.jmedchem.1c01273" }
    ],
    nextSteps: [
      "Pilot 10 g synthesis from isolated corynantheidine",
      "MOR/KOR/DOR Ki panel",
      "Rat oral PK vs unfluorinated parent",
      "Hot-plate antinociception dose-response"
    ]
  },

  "mitragynine-ester-analogs": {
    id: "mitragynine-ester-analogs",
    name: "Mitragynine Ester Analogs",
    fullName: "C16 ester SAR class — mitragynine PK/potency modulation",
    categoryId: "mitragynine",
    program: "Program A — Kratom / Harm Reduction",
    cas: null,
    formula: "Variable (ester chain)",
    molecularWeight: "Variable",
    schedulingStatus: "unscheduled-federal",
    schedulingNote: "Unscheduled US federal; mitragynine-class Analogue-Act exposure.",
    benefitScore: 66,
    harmScore: 28,
    evidenceConfidence: "LOW-MEDIUM",
    status: "GO",
    statusColor: "green",
    goDecision: "GO — ester SAR class enables prodrug/PK modulation and oral bioavailability tuning.",
    priorityTier: "Tier 3",
    summary: "Mitragynine contains a C16 methyl ester that is a site for PK/potency modulation. Replacing methyl with longer-chain, branched, or hetero-ester groups tunes oral bioavailability, plasma half-life, and MOR potency. Class gives access to prodrug strategies and improved drug-likeness.",
    pros: [
      "Single-step synthesis from mitragynine via transesterification",
      "Enables prodrug or PK-extension strategies",
      "SAR class with well-defined MOR SAR anchor (mitragynine)"
    ],
    cons: [
      "Ester hydrolysis to parent mitragynine limits true differentiation for some analogs",
      "Plasma esterase variability across species complicates translation",
      "Class scheduling pressure"
    ],
    pharmacology: {
      mechanismOfAction: "MOR partial agonist (like parent); ester modification primarily tunes PK, not pharmacology.",
      targets: [
        { target: "MOR", affinity: "Ki ~50 nM (class range)", efficacy: "Partial agonist", source: "Kruegel 2016 SAR" }
      ]
    },
    labSynthesis: {
      overview: "Mitragynine methyl ester hydrolysis (K2CO3/MeOH — not Wohler-standard; use NaOH/EtOH instead) → carboxylic acid → reprotonate with desired alcohol via DCC/DMAP coupling.",
      steps: [
        { step: 1, reagent: "Mitragynine + NaOH (3 eq), EtOH/H2O (4:1), reflux 4 h", product: "Mitragynine C16 carboxylic acid" },
        { step: 2, reagent: "DCC (1.2 eq), DMAP (0.1 eq), R-OH (5 eq), EtOAc, rt, 12 h", product: "Mitragynine C16 ester (R-alkyl, aryl, etc.)" }
      ]
    },
    kgScaleSynthesis: {
      overview: "Semi-synthesis from mitragynine. Alternative one-pot transesterification with Ti(OiPr)4 and R-OH at higher temperature.",
      targetScale: "200 g per variant",
      steps: [
        { stage: "Saponification", solvent: "EtOH/H2O", notes: "NaOH; neutralize to pH 5 and extract with EtOAc." },
        { stage: "Esterification", solvent: "EtOAc", notes: "DCC/DMAP or Ti(OiPr)4 catalysis." },
        { stage: "Crystallization", solvent: "IPA or EtOH", notes: "Variable — optimize per analog." }
      ],
      yieldTarget: "40–70% depending on ester.",
      standardSolventsUsed: ["EtOH", "EtOAc", "IPA", "THF"],
      standardSolventsNotUsed: ["Hexane", "Heptane", "Pentane", "MeOH", "Acetone"],
      hazards: "DCC skin sensitizer; opioid-class containment."
    },
    safety: {
      ehs: "Opioid-grade containment.",
      toxicology: "Variable by analog; plasma hydrolysis profile is key safety consideration.",
      scheduling: "Unscheduled US federal; Analogue-Act exposure."
    },
    wohlerScoring: {
      benefit: 66, harm: 28, net: 38,
      evidenceConfidence: "LOW-MEDIUM",
      rationale: "Useful PK-modulation toolkit but lower mechanistic novelty than pseudoindoxyl or biased-agonist classes."
    },
    references: [
      { citation: "Kruegel A.C. et al. JACS 2016, 138(21), 6754–6764.", doi: "10.1021/jacs.6b00360" }
    ],
    nextSteps: [
      "Design 6-compound ester SAR set (Me / Et / iPr / nBu / CH2CF3 / Ph)",
      "In-vitro plasma hydrolysis rate (human / rat / mouse)",
      "MOR potency panel on each ester",
      "Rat oral PK on lead ester(s)"
    ]
  }
};